import {noop} from 'lodash';
import React, {FC, useCallback, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {useDataEventTracking} from '../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {InfoBubbleInstanceProps} from '../../../libs/shared/ui/src/components/tip/infoBubble';
import {InfoBubblePillVariantsEnum} from '../../../libs/shared/ui/src/components/tip/infoBubblePill';
import teamWorkspaceImage from '../../assets/images/teamWorkspaceTip.svg';
import {useAppDispatch, useAppSelector} from '../../helpers/appReduxHelpers';
import {OnboardingTipTypesEnum} from '../../models/api/preferencesModel';
import {generateNewSandboxConversationAction} from '../../store/data/conversations/sandboxConversationsActions';
import {
  navigateToExpandedSandboxInboxAction,
  togglePinSandboxTeamAsyncAction,
} from '../../store/data/teammates/teammatesActions';
import {getHasSandbox} from '../../store/data/teams/teamsSelectors';
import {getAvailableSetupGuide, SetupGuideEnum} from '../../store/stateless/onboardingSelectors';
import {useOnboardingModalState} from '../app/topBar/onboarding/onboardingContext';
import {EducationTip} from '../educationTips/educationTip';
import {OnboardingGuidesEnum} from '../onboarding/guide/onboardingGuideTypes';
import {generateStepText} from '../onboarding/welcome/onboardingWelcomeHelpers';
import {useOnboardingInfoBubblePresenter} from './onboardingInfoBubbleHook';

/*
 * Styles.
 */

const StyledImg = styled.img`
  display: block;
  width: 380px;
  height: 142px;
`;

/*
 * Component.
 */

export const TeamWorkspaceInfoBubble: FC<InfoBubbleInstanceProps> = ({anchor}) => {
  const {isVisible, closeInfoBubble: onClose} = useOnboardingInfoBubblePresenter(
    OnboardingTipTypesEnum.TEAM_WORKSPACE,
  );
  const {setOnboardingGuide} = useOnboardingModalState();
  const dispatch = useAppDispatch();
  const hasUserSandboxAccess = useAppSelector(getHasSandbox);
  const setupGuide = useAppSelector(getAvailableSetupGuide);
  const totalSteps = hasUserSandboxAccess ? 3 : 2;

  const primaryActionLabel = hasUserSandboxAccess ? (
    <Trans id="infobubble-teamworkspace-next-cta">Next</Trans>
  ) : (
    <Trans id="infobubble-teamworkspace-done-cta">Got it!</Trans>
  );

  // We need some time to generate a demo conversation
  // so, in order to have a nice UX, we prepare it when this tip becomes visible.
  const sandboxConversationPromiseRef = useRef<Promise<unknown>>();

  useEffect(() => {
    const prepareSandboxConversationAsync = async () => {
      if (sandboxConversationPromiseRef.current) {
        return;
      }

      // Re-pin sandbox team if it was previously hidden.
      const prepareSandbox = dispatch(togglePinSandboxTeamAsyncAction(true)).then(() =>
        dispatch(generateNewSandboxConversationAction(undefined)),
      );
      sandboxConversationPromiseRef.current = prepareSandbox;
    };
    if (isVisible && hasUserSandboxAccess) {
      prepareSandboxConversationAsync();
    }
  }, [dispatch, hasUserSandboxAccess, isVisible]);

  const {trackDataEvent} = useDataEventTracking();
  const onPrimaryClick = useCallback(async () => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ONBOARDING_TEAM_WORKSPACE_NEXT, {
      has_sandbox_access: hasUserSandboxAccess,
    });

    if (hasUserSandboxAccess) {
      try {
        // Re-pin sandbox team if it was previously hidden.
        await dispatch(togglePinSandboxTeamAsyncAction(true));

        if (sandboxConversationPromiseRef.current) {
          await sandboxConversationPromiseRef.current;
        }

        dispatch(navigateToExpandedSandboxInboxAction());
      } catch (error) {
        logger.error('Failed sandbox navigation', {error});
      }

      onClose();
      return;
    }

    if (setupGuide === SetupGuideEnum.FRONT_LINE_USER) {
      setOnboardingGuide(OnboardingGuidesEnum.FRONT_LINE_USER_SETUP);
      onClose();
      return;
    }

    onClose();
  }, [dispatch, hasUserSandboxAccess, onClose, trackDataEvent]);

  if (!isVisible) {
    return null;
  }

  return (
    <EducationTip
      anchor={anchor}
      position="right-start"
      isInteractive
      isVisible={isVisible}
      onRequestClose={noop}
      hero={<StyledImg src={teamWorkspaceImage} alt="Team workspace illustration" />}
      pillType={InfoBubblePillVariantsEnum.EDUCATION}
      title={<Trans id="infobubble-teamworkspace-title">Here is your team&apos;s workspace</Trans>}
      primaryActionLabel={primaryActionLabel}
      onPrimaryClick={onPrimaryClick}
      stepCounter={generateStepText(2, totalSteps)}
      dataObjectName={OnboardingDataEventObject.ONBOARDING_TEAM_WORKSPACE}
    >
      <Trans id="infobubble-teamworkspace-description">
        All of your shared inboxes live here. Everyone on your workspace can see these messages.
      </Trans>
    </EducationTip>
  );
};
